import { FC, useLayoutEffect, useState, Dispatch, SetStateAction, useRef, useEffect, useCallback } from "react";
import { Controller } from "react-hook-form";
import ReactSelect from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import { Tooltip, Collapse } from '@mui/material';
import { useForm } from "react-hook-form";

import useStickyClass from "../../../../../../hooks/useStickyClass";
import MenuView from "./MenuView";
import { ViewName } from "../../../../components/Cards/templateCard/types";
import { IconAdjustmentsAlt, IconSearch, IconX, IconArrowsSort } from "@tabler/icons-react";
import { ProjectDetailType } from "../types";
import Button from "../../../../../../components/form/Button";

import { useDetailProjectInvestors } from "../../hooks/useDetailProjectInvestors";

type OptionType = { label: string; value: string | number };

type FiltersType = {
    area: string;
    bedrooms: string;
    landlord_id: string;
    level: string;
    model_id: string;
    status: string;
    unit_name: string;
    page: number;
    take: number;
}

interface FiltersProps {
    viewName: ViewName;
    setViewName: Dispatch<SetStateAction<ViewName>>;
    detailProject: ProjectDetailType;
    sort: string;
    setSort: Dispatch<SetStateAction<string>>;
    setFilters: Dispatch<SetStateAction<Record<string, string | number>>>;
    isDepto: boolean;
}

export const DefaultFilters: FiltersType = {
    area: "0",
    bedrooms: "0",
    landlord_id: "0",
    level: "0",
    model_id: "0",
    status: "0",
    unit_name: "",
    page: 1,
    take: 30,
}

const Filters: FC<FiltersProps> = ({ setViewName, viewName, sort, setSort, setFilters, detailProject, isDepto }) => {
    const filtersRef = useRef<HTMLDivElement>(null);
    useStickyClass(filtersRef, "shadow-bottom");

    const navigate = useNavigate();
    const params = useParams();
    const { id = "" } = useParams();

    const { investors, investorsLoading, getDataInvestor } = useDetailProjectInvestors();

    const [showFilters, setShowFilters] = useState(false);

    const all = { label: "Todos", value: "0" };

    // Formatear listas
    const listInvestors: OptionType[] = investors?.map(({ name, id }) => ({ label: name, value: id }));
    const listModels: OptionType[] = detailProject?.filtersSettings?.by_model?.map((model: any) => ({ label: model.value, value: model.key })) || [];
    const listStatus: OptionType[] = detailProject?.filtersSettings?.by_status?.map((status: any) => ({ label: status.value, value: status.key })) || [];
    const listAreas: OptionType[] = detailProject?.filtersSettings?.by_area?.map((area: any) => ({ label: area, value: area })) || [];
    const listLevels: OptionType[] = detailProject?.filtersSettings?.by_level?.map((level: any) => ({ label: level, value: level })) || [];
    const listBedrooms: OptionType[] = detailProject?.filtersSettings?.by_bedrooms?.map((bedroom: any) => ({ label: bedroom, value: bedroom })) || [];

    const onSubmit = (values: FiltersType) => {
        setFilters(values);
        // Setear los valores del formulario como parámetros en la URL, omitiendo los que sean "0"
        const searchParams = new URLSearchParams(window.location.search);
        // Agregar/actualizar los valores del formulario
        Object.entries(values)
            .filter(([_, v]) => v !== undefined && v !== "" && v !== null && v !== "0")
            .forEach(([k, v]) => {
                searchParams.set(k, String(v));
            });
        // Conservar los parámetros que no estén en DefaultFilters
        Object.keys(DefaultFilters).forEach(k => {
            // Si el valor es "0" o vacío, eliminar el parámetro
            if (values[k as keyof FiltersType] === undefined || values[k as keyof FiltersType] === "" || values[k as keyof FiltersType] === null || values[k as keyof FiltersType] === "0") {
                searchParams.delete(k);
            }
        });
        navigate(`?${searchParams.toString()}`, { replace: true });
    }

    const {
        handleSubmit: handleSubmitForm,
        reset,
        control,
        setValue,
        getValues,
    } = useForm({
        defaultValues: DefaultFilters
    });

    const setFiltersFromParams = useCallback(
        () => {
            // Setear los valores del formulario basados en los parámetros de la URL
            setValue("area", params?.area as string);
            setValue("level", params?.level as string);
            setValue("bedrooms", params?.bedrooms as string);
            setValue("model_id", params?.model_id as string);
            setValue("status", params?.status as string);
            setValue("landlord_id", params?.landlord_id as string);
            setValue("unit_name", params?.unit_name as string);
        },
        [],
    )

    useEffect(() => {
        setFiltersFromParams();
    }, [setFiltersFromParams])

    const clearFilter = () => {
        reset();
        handleSubmitForm(onSubmit)();
    };

    const getSortName = () => {
        let msg = "";
        if (viewName === "Tower") {
            msg = sort === "asc" ? "Ordenado de menor a mayor" : "Ordenado de mayor a menor";
        } else {
            msg = sort === "asc" ? "Ordenado de A a Z" : "Ordenado por fecha de actualización";
        }

        return msg;
    }

    const handleSort = () => {
        setSort(sort === "asc" ? "desc" : "asc");
    }

    return (
        <aside
            ref={filtersRef}
            className={`filters-devs w-full self-start sticky top-11 md:top-14 z-10 bg-[#f6f6f6] py-4 px-4 2xl:px-0 block`}
            onMouseLeave={() => setShowFilters(false)}
        >
            <form className="flex flex-col justify-between items-start w-full" onSubmit={handleSubmitForm(onSubmit)}>
                <div className="flex flex-row items-start md:items-center justify-end w-full md:w-full gap-2">
                    <div className="flex-1 flex flex-col relative justify-between items-center max-w-[300px] w-full">
                        <Controller
                            name="unit_name"
                            control={control}
                            defaultValue={getValues("unit_name")}
                            render={({ field: { onChange, value } }) => (
                                <input
                                    className="w-full text-sm focus:outline-none border border-blackBase rounded-md text-[#212121] py-2 px-2"
                                    type="text"
                                    value={value ?? ""}
                                    onChange={e => onChange(e.target.value)}
                                    placeholder="Buscar por nombre de unidad"
                                />
                            )}
                        />
                        <Button
                            type="submit"
                            variant="transparent"
                            className="absolute top-[1px] right-[1px] !ring-0 !shadow-none !bg-white"
                        >
                            <IconSearch className="size-6" />
                        </Button>
                    </div>

                    <Tooltip title={showFilters ? "Ocultar filtros" : "Mostrar filtros"} arrow placement="top">
                        <Button
                            onClick={() => setShowFilters((show) => !show)}
                            variant="outlined"
                        >
                            <IconAdjustmentsAlt className="size-6" />
                        </Button>
                    </Tooltip>
                    <Tooltip title={"Cambiar modo de vista"} arrow placement="top">
                        <div>
                            <MenuView
                                setViewName={setViewName}
                                viewName={viewName}
                                isDepto={isDepto}
                            />
                        </div>
                    </Tooltip>
                    <Tooltip title={getSortName()} arrow placement="top">
                        <Button
                            onClick={handleSort}
                            variant="outlined"
                        >
                            <IconArrowsSort className="size-6" />
                        </Button>
                    </Tooltip>
                </div>

                <Collapse in={showFilters} className="w-full">
                    <div className=" rounded-md p-6 mt-4 bg-white shadow-sm">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            <div className="col-span-1 flex flex-col w-full mt-0 lg:mt-0">
                                <label className="text-xs font-medium text-[#212121]">
                                    Propietario
                                </label>
                                <Controller
                                    name="landlord_id"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <ReactSelect
                                            options={[all, ...listInvestors]}
                                            value={[all, ...listInvestors].find(opt => opt.value === value) || null}
                                            onChange={opt => onChange(opt?.value)}
                                            placeholder="Buscar por propietario"
                                            isSearchable
                                            className="[&>div>div>div]:truncate"
                                            isLoading={investorsLoading}
                                            onMenuOpen={() => getDataInvestor(id)}
                                        />
                                    )}
                                />
                            </div>
                            {listModels.length > 0 && (
                                <div className="col-span-1 flex flex-col w-full mt-0 lg:mt-0">
                                    <label className="text-xs font-medium text-[#212121]">
                                        Modelo
                                    </label>
                                    <Controller
                                        name="model_id"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <ReactSelect
                                                options={[all, ...listModels]}
                                                value={[all, ...listModels].find(opt => opt.value === value) || null}
                                                onChange={opt => onChange(opt?.value)}
                                                placeholder="Buscar por modelo"
                                                isSearchable
                                                className="[&>div>div>div]:truncate"
                                            />
                                        )}
                                    />
                                </div>
                            )}
                            {listStatus.length > 0 && (
                                <div className="col-span-1 flex flex-col w-full mt-0 lg:mt-0">
                                    <label className="text-xs font-medium text-[#212121]">
                                        Estado
                                    </label>
                                    <Controller
                                        name="status"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <ReactSelect
                                                options={[all, ...listStatus]}
                                                value={[all, ...listStatus].find(opt => opt.value === value) || null}
                                                onChange={opt => onChange(opt?.value)}
                                                placeholder="Buscar por estado"
                                                isSearchable
                                                className="[&>div>div>div]:truncate"
                                            />
                                        )}
                                    />
                                </div>
                            )}
                            {listAreas.length > 0 && (
                                <div className="col-span-1 flex flex-col w-full mt-0 lg:mt-0">
                                    <label className="text-xs font-medium text-[#212121]">
                                        Área
                                    </label>
                                    <Controller
                                        name="area"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <ReactSelect
                                                options={[all, ...listAreas]}
                                                value={[all, ...listAreas].find(opt => opt.value === value) || null}
                                                onChange={opt => onChange(opt?.value)}
                                                placeholder="Buscar por área"
                                                isSearchable
                                                className="[&>div>div>div]:truncate"
                                            />
                                        )}
                                    />
                                </div>
                            )}
                            {listLevels.length > 0 && (
                                <div className="col-span-1 flex flex-col w-full mt-0 lg:mt-0">
                                    <label className="text-xs font-medium text-[#212121]">
                                        Nivel
                                    </label>
                                    <Controller
                                        name="level"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <ReactSelect
                                                options={[all, ...listLevels]}
                                                value={[all, ...listLevels].find(opt => opt.value === value) || null}
                                                onChange={opt => onChange(opt?.value)}
                                                placeholder="Buscar por nivel"
                                                isSearchable
                                                className="[&>div>div>div]:truncate"
                                            />
                                        )}
                                    />
                                </div>
                            )}
                            {listBedrooms.length > 0 && (
                                <div className="flex flex-col w-full mt-0 lg:mt-0">
                                    <label className="text-xs font-medium text-[#212121]">
                                        Habitaciones
                                    </label>
                                    <Controller
                                        name="bedrooms"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <ReactSelect
                                                options={[all, ...listBedrooms]}
                                                value={[all, ...listBedrooms].find(opt => opt.value === value) || null}
                                                onChange={opt => onChange(opt?.value)}
                                                placeholder="Buscar por habitaciones"
                                                isSearchable
                                                className="[&>div>div>div]:truncate"
                                            />
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                            <div className="col-span-1" />
                            <div className="col-span-1" />
                            <div className="col-span-1 flex justify-start items-center mt-4">
                                <Button
                                    type="submit"
                                    className="w-full"
                                >
                                    <span className="text-sm lg:text-base">Filtrar</span>
                                </Button>
                            </div>
                            <div className="col-span-1 flex justify-start items-center mt-4">
                                <Button
                                    onClick={clearFilter}
                                    className="w-full"
                                    startIcon={<IconX className="size-5" />}
                                    variant="outlined"
                                >
                                    <span className="text-sm lg:text-base">Limpiar filtros</span>
                                </Button>
                            </div>
                        </div>
                    </div>
                </Collapse>
            </form>
        </aside >
    )
}

export default Filters